import { motion, AnimatePresence } from "motion/react";
import { 
  X, 
  Truck, 
  User, 
  Phone, 
  MapPin, 
  Clock, 
  Thermometer, 
  Package,
  Calendar,
  Navigation,
  CheckCircle,
  AlertCircle
} from "lucide-react";

interface DeliveryDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  delivery: {
    batchId: string;
    vehicleNumber: string;
    driverName: string;
    departureTime: string;
    arrivalTime: string;
    distance: string;
    temperature: number;
    status: "in-transit" | "delivered" | "pending";
    imageUrl: string;
    // Additional details
    driverPhone?: string;
    sourceLocation?: string;
    destinationLocation?: string;
    concreteGrade?: string;
    quantity?: string;
    departureDate?: string;
    customerName?: string;
    projectSite?: string;
  } | null;
  onCallDriver: () => void;
  onTrackLive: () => void;
}

export default function DeliveryDetailModal({
  isOpen,
  onClose,
  delivery,
  onCallDriver,
  onTrackLive,
}: DeliveryDetailModalProps) {
  if (!delivery) return null;

  const statusConfig = {
    "in-transit": {
      icon: Truck,
      bg: "bg-blue-50",
      text: "text-blue-600",
      label: "In Transit",
      badge: "bg-blue-500",
    },
    delivered: {
      icon: CheckCircle,
      bg: "bg-green-50",
      text: "text-green-600",
      label: "Delivered",
      badge: "bg-green-500",
    },
    pending: {
      icon: Clock,
      bg: "bg-amber-50",
      text: "text-amber-600",
      label: "Pending",
      badge: "bg-amber-500",
    },
  };

  const config = statusConfig[delivery.status];
  const StatusIcon = config.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Bottom Sheet Modal */}
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center md:p-4 pointer-events-none pb-16 md:pb-0">
            <motion.div
              initial={{ opacity: 0, y: "100%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "100%" }}
              transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
              className="bg-white rounded-t-3xl md:rounded-2xl w-full md:max-w-3xl max-h-[85vh] md:max-h-[90vh] overflow-hidden pointer-events-auto flex flex-col"
              style={{ boxShadow: "0 -10px 40px rgba(0, 0, 0, 0.15), 0 20px 50px rgba(0, 0, 0, 0.3)" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag Handle (Mobile Only) */}
              <div className="md:hidden flex justify-center py-3 bg-white">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
                <div>
                  <h2 className="text-slate-900" style={{ fontSize: "20px", fontWeight: "700" }}>
                    Delivery Details
                  </h2>
                  <p className="text-slate-500" style={{ fontSize: "14px" }}>
                    {delivery.batchId}
                  </p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </motion.button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto" style={{ paddingBottom: "80px" }}>
                <div className="p-5 space-y-5">
                  
                  {/* Status Banner */}
                  <div className={`${config.bg} rounded-2xl p-4`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`${config.badge} p-2.5 rounded-xl`}>
                          <StatusIcon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className={`${config.text} mb-0.5`} style={{ fontSize: "16px", fontWeight: "700" }}>
                            {config.label}
                          </div>
                          <p className="text-slate-600" style={{ fontSize: "13px" }}>
                            {delivery.status === "in-transit" && "Vehicle is on the way"}
                            {delivery.status === "delivered" && "Successfully delivered"}
                            {delivery.status === "pending" && "Awaiting dispatch"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Batch Information */}
                  <div>
                    <h3 className="text-slate-900 mb-3" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Batch Information
                    </h3>
                    <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <Package className="w-4 h-4 text-slate-500 mt-1" />
                        <div className="flex-1">
                          <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>Grade</div>
                          <div className="text-slate-900" style={{ fontWeight: "600" }}>
                            {delivery.concreteGrade || "M30"}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Package className="w-4 h-4 text-slate-500 mt-1" />
                        <div className="flex-1">
                          <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>Quantity</div>
                          <div className="text-slate-900" style={{ fontWeight: "600" }}>
                            {delivery.quantity || "6 m³"}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Thermometer className="w-4 h-4 text-slate-500 mt-1" />
                        <div className="flex-1">
                          <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>Temperature</div>
                          <div className="text-slate-900" style={{ fontWeight: "600" }}>
                            {delivery.temperature}°C
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Vehicle & Driver */}
                  <div>
                    <h3 className="text-slate-900 mb-3" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Vehicle & Driver
                    </h3>
                    <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <Truck className="w-4 h-4 text-slate-500 mt-1" />
                        <div className="flex-1">
                          <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>Vehicle Number</div>
                          <div className="text-slate-900" style={{ fontWeight: "600" }}>
                            {delivery.vehicleNumber}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <User className="w-4 h-4 text-slate-500 mt-1" />
                        <div className="flex-1">
                          <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>Driver Name</div>
                          <div className="text-slate-900" style={{ fontWeight: "600" }}>
                            {delivery.driverName}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Phone className="w-4 h-4 text-slate-500 mt-1" />
                        <div className="flex-1">
                          <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>Contact Number</div>
                          <div className="text-slate-900" style={{ fontWeight: "600" }}>
                            {delivery.driverPhone || "+91 98765 43210"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Delivery Schedule */}
                  <div>
                    <h3 className="text-slate-900 mb-3" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Delivery Schedule
                    </h3>
                    <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <Calendar className="w-4 h-4 text-slate-500 mt-1" />
                        <div className="flex-1">
                          <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>Date</div>
                          <div className="text-slate-900" style={{ fontWeight: "600" }}>
                            {delivery.departureDate || "Dec 30, 2024"}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Clock className="w-4 h-4 text-slate-500 mt-1" />
                        <div className="flex-1">
                          <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>Departure Time</div>
                          <div className="text-slate-900" style={{ fontWeight: "600" }}>
                            {delivery.departureTime}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Clock className="w-4 h-4 text-slate-500 mt-1" />
                        <div className="flex-1">
                          <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>Expected Arrival</div>
                          <div className="text-slate-900" style={{ fontWeight: "600" }}>
                            {delivery.arrivalTime}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Navigation className="w-4 h-4 text-slate-500 mt-1" />
                        <div className="flex-1">
                          <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>Distance</div>
                          <div className="text-slate-900" style={{ fontWeight: "600" }}>
                            {delivery.distance}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location Details */}
                  <div>
                    <h3 className="text-slate-900 mb-3" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Location Details
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-green-500 mt-1" />
                          <div className="flex-1">
                            <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>Source Location</div>
                            <div className="text-slate-900" style={{ fontWeight: "600" }}>
                              {delivery.sourceLocation || "RMC Plant - Sector 18, Mumbai"}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-red-500 mt-1" />
                          <div className="flex-1">
                            <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>Destination</div>
                            <div className="text-slate-900" style={{ fontWeight: "600" }}>
                              {delivery.destinationLocation || delivery.projectSite || "Construction Site - Andheri West"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Customer Information */}
                  <div>
                    <h3 className="text-slate-900 mb-3" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Customer Information
                    </h3>
                    <div className="bg-slate-50 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <User className="w-4 h-4 text-slate-500 mt-1" />
                        <div className="flex-1">
                          <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>Customer Name</div>
                          <div className="text-slate-900" style={{ fontWeight: "600" }}>
                            {delivery.customerName || "Horizon Constructions Pvt. Ltd."}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Footer Actions */}
              <div className="p-5 border-t border-slate-100 bg-white flex-shrink-0">
                <div className="grid grid-cols-2 gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      onCallDriver();
                      onClose();
                    }}
                    className="py-3.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl transition-all font-semibold flex items-center justify-center gap-2"
                  >
                    <Phone className="w-4 h-4" />
                    Call Driver
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      onTrackLive();
                      onClose();
                    }}
                    className="py-3.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-all font-semibold flex items-center justify-center gap-2 shadow-lg shadow-blue-500/30"
                  >
                    <Navigation className="w-4 h-4" />
                    Track Live
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
